// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Box } from 'grommet'
import { useNavigate } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { CircleInformation } from 'grommet-icons'
import { useReactOidc } from '@axa-fr/react-oidc-context'

import {
  DataTable,
  Typography,
  NoDataInfo,
  Notification
} from '../../../../../../components'
import TruncateButton from '../../../../../device-management/components/TruncateButton'
import { ManageSubscriptionTagsModal } from '../../../common-components/ManageSubscriptionTags'
import { isServiceCentric } from '../../../../../../utils/account-utils'
import {
  getOnPremWorkSpaces,
  NO_VALUE,
  showAssignToOnPremWorkspaceBtn
} from '../../../../../../utils/dm-sm-common-utils'
import { isCoP, isGLOP } from '../../../../../../utils/feature-flag-utils'
import VisibilityWrapper from '../../../../../../commoncomponents/visibility-wrapper/VisibilityWrapper'
import {
  getAssignToOnPremWorkSpacePayload,
  getSubsExportPermission
} from '../../../utils'
import { customRenderer } from '../../../../../../commoncomponents/CustomRenderer'
import { AssignToOnPremWorkSpaceModal } from '../../../../../../commoncomponents/device-management'
import { useVisibilityContext } from '../../../../../../context/visibility-context'
import { RemoveSubscriptionModal } from '../../licenses/components/RemoveSubscriptionModal'
import OnpremAssignmentSuccessModal from '../../../../../device-management/components/OnpremAssignmentSuccessModal'
import SubscriptionsFilterButton from '../../licenses/components/SubscriptionsFilterButton'

import SideDetailsPanel from './SideDetailsPanel'

const ServiceSubscriptionsDatatable = ({
  serviceSubs,
  appData,
  pagination,
  setRefreshDataTable,
  refreshDataTable,
  setSearchTerm,
  setPage,
  tabName,
  setShowExportModal,
  sortProps,
  setSortProps,
  setNotification,
  filterOptions,
  setFilterOptions
}) => {
  const { t } = useTranslation(['device', 'common', 'licensing', 'manage'])
  const LDFlags = useFlags()
  const newDashboardFlag = isServiceCentric(LDFlags)
  const { oidcUser } = useReactOidc()
  const { rbacPolicies } = useVisibilityContext()
  const [pullTagsRefresh, setPullTagsRefresh] = useState(false)
  const {
    'glcp-glasgow-reportfw-subscriptions-export':
      reportSubscriptionsExportLdFlag,
    'glcp-subscrption-tags': isServiceSubscriptionFlag,
    'glcp-subscription-expiry': subscriptionExpiryFlag,
    'glcp-dscc-dm-sm': glopDevicesPreOnBoardingFlag
  } = useFlags()
  const [rowDetails, setRowDetails] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [showDetailsPanel, setShowDetailsPanel] = useState(false)
  const [manageTagsModal, setManageTagsModal] = useState(false)
  const [manageTagsData, setManageTagsData] = useState([])
  const [showErrorNotification, setShowErrorNotification] = useState(null)
  const navigate = useNavigate()
  const handleRowCheckBoxSelection = (selectedRow) => {
    setSelectedRows(selectedRow)
  }
  const handleManageModalOpen = (open) => {
    setManageTagsModal(open)
  }
  const [showOnPremWorkSpaceModal, setOnPremWorkSpaceModal] = useState(false)
  const [successNotification, setSuccessNotification] = useState(null)
  const [onPremWorkSpaceData, setOnPremSpaceData] = useState([])
  const [showRemoveSubscriptionModal, setRemoveSubscriptionModal] =
    useState(false)
  const [
    showSubscriptionFeedbackNotification,
    setSubscriptionFeedbackNotification
  ] = useState(false)

  const handleRemoveSubscriptionModalOpen = (open) => {
    setRemoveSubscriptionModal(open)
  }

  useEffect(() => {
    if (glopDevicesPreOnBoardingFlag) {
      getOnPremWorkSpaces(
        oidcUser.access_token,
        t,
        setShowErrorNotification
      ).then((data) => {
        setOnPremSpaceData(data?.items)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glopDevicesPreOnBoardingFlag])

  const columns = [
    {
      property: 'subscription_key',
      type: 'string',
      header: t('subscription_key'),
      render: (datum) => (
        <Box direction="row" align="center">
          <Typography
            type="text"
            emphasis
            size="medium"
            wordBreak="break-all"
            testId="subscription-key"
          >
            {datum?.subscription_key}
          </Typography>
        </Box>
      ),
      primary: true
    },
    ...(LDFlags['glcp-show-service-subscription-tier'] || isGLOP()
      ? [
          {
            property: 'subscription_tier_description',
            type: 'string',
            header: t('tier'),
            render: (datum) => datum?.subscription_tier_description || NO_VALUE
          }
        ]
      : []),
    {
      property: 'product_description',
      type: 'string',
      header: t('subscription_name')
    },
    {
      property: 'subscription_type',
      type: 'string',
      header: t('subscription_type')
    },
    {
      property: 'expiration',
      type: 'date',
      align: isServiceSubscriptionFlag ? 'start' : '',
      header: t('expiration_date'),
      sortable:
        LDFlags[
          ('glcp-subscriptions-expiry-sorting', 'glcp-subscription-expiry')
        ] ||
        isCoP() ||
        isGLOP()
    },
    ...(isServiceSubscriptionFlag && newDashboardFlag
      ? [
          {
            property: 'tags',
            type: 'string',
            header: t('tags.tags_title'),
            render: (datum) => {
              return <TruncateButton datum={datum} type="ServiceLicense" />
            }
          }
        ]
      : []),
    ...(glopDevicesPreOnBoardingFlag && onPremWorkSpaceData?.length
      ? [
          {
            property: 'on_prem_workspace_name',
            type: 'string',
            header: t('onprem_workspace'),
            render: (datum) => {
              return datum?.on_prem_workspace_name || '--'
            }
          }
        ]
      : [])
  ]

  useEffect(() => {
    if (isEmpty(filterOptions)) {
      setSelectedRows([])
    }
  }, [filterOptions])

  const formatRowDetails = (data) => {
    const formattedData = { ...data.appointments, ...data }
    delete formattedData.appointments
    return formattedData
  }

  return (
    <>
      <Box gap="xlarge" flex>
        <DataTable
          grid={{
            columns,
            resizeable: !!isServiceSubscriptionFlag || isGLOP(),
            data: serviceSubs,
            onClickRow: (data) => {
              setRowDetails(formatRowDetails(data?.datum))
              if ((isServiceSubscriptionFlag && newDashboardFlag) || isGLOP())
                navigate(
                  `/services/service-subscriptions/${data?.datum?.subscription_key}`
                )
              else setShowDetailsPanel(true)
            },
            onSort: ({ property, direction, external }) => {
              if (subscriptionExpiryFlag || isGLOP()) {
                setSortProps({ property, direction, external })
                setPage(1)
              }
            },
            sort: sortProps
          }}
          search={{
            width: 'medium',
            onSearchValueChange: (val) => {
              setSearchTerm(val)
              setPage(1)
            },
            placeholder: t('common:search_by_subscription_key')
          }}
          summary={
            (subscriptionExpiryFlag || isGLOP()) && {
              summaryText: t('common:data_table.items', {
                numOfItems: pagination
                  ? pagination?.totalItems
                  : serviceSubs && serviceSubs?.length
              })
            }
          }
          selection={
            newDashboardFlag
              ? {
                  bulkActions: {
                    actionDropdown: {
                      label: t('common:actions'),
                      visibility: {
                        rbac: {
                          resource: '/ccs/device-management',
                          permission: 'ccs.device-management.edit'
                        }
                      }
                    },
                    customRenderer: (element, visibility, index) =>
                      customRenderer(element, visibility, index),
                    actions: [
                      {
                        label: t('device:export'),
                        onClick: () => {
                          setNotification(null)
                          setShowExportModal(true)
                        },
                        hidden:
                          (!reportSubscriptionsExportLdFlag &&
                            !subscriptionExpiryFlag) ||
                          isGLOP() ||
                          !getSubsExportPermission(rbacPolicies),
                        testId: 'export-btn',
                        selectionBased: false
                      },
                      {
                        label: t('manage_tags'),
                        onClick: () => {
                          setNotification(null)
                          const data = serviceSubs?.filter((temp) =>
                            selectedRows.includes(temp?.subscription_key)
                          )
                          setManageTagsData(data)
                          setManageTagsModal(true)
                        },
                        hidden: !isServiceSubscriptionFlag || isGLOP(),
                        testId: 'manage-tags-btn'
                      },
                      {
                        label: t('manage:onprem_workspace.assign_to_onprem'),
                        onClick: () => {
                          setOnPremWorkSpaceModal(true)
                        },
                        hidden:
                          isCoP() ||
                          isGLOP() ||
                          !glopDevicesPreOnBoardingFlag ||
                          !showAssignToOnPremWorkspaceBtn(
                            selectedRows,
                            serviceSubs,
                            onPremWorkSpaceData,
                            true
                          ),
                        testId: 'assign-to-onprem-workspace-btn'
                      },
                      {
                        label: t('remove_subscriptions_btn'),
                        onClick: () => {
                          setRemoveSubscriptionModal(true)
                        },
                        visibility: {
                          hideFor: {
                            deployment: ['COP', 'GLOP'],
                            feature:
                              'glcp-enable-unclaim-and-transfer-subscriptions'
                          }
                        },
                        testId: 'remove-subscriptions-btn'
                      }
                    ]
                  },
                  onSelectionChange: (selectedRow) =>
                    handleRowCheckBoxSelection(selectedRow),
                  selectionInfoMessage: t('common:data_table_warning_msg'),
                  primaryKey: 'subscription_key'
                }
              : {}
          }
          pagination={pagination}
          filterButton={
            <VisibilityWrapper
              hideFor={{ feature: 'glcp-subscription-expiry' }}
            >
              <Box>
                <SubscriptionsFilterButton
                  productType="SERVICE"
                  tabName={tabName}
                  onFilterValueChange={(filters) => {
                    setFilterOptions(filters)
                    setPage(1)
                  }}
                  filterOptions={filterOptions}
                  refreshTags={pullTagsRefresh}
                />
              </Box>
            </VisibilityWrapper>
          }
          testId="service-subscriptions-datatable"
        />
        {serviceSubs.length === 0 && (
          <NoDataInfo
            icon={<CircleInformation size="xlarge" />}
            title={t('licensing:card_empty_state_title')}
            subtitle={
              subscriptionExpiryFlag
                ? t('licensing:card_empty_state_subtitle')
                : null
            }
            testId="no-tab-data-total-service-subs"
          />
        )}
      </Box>
      {showDetailsPanel && (
        <SideDetailsPanel
          data={rowDetails}
          appData={appData}
          onClose={(e) => setShowDetailsPanel(e)}
        />
      )}
      {showOnPremWorkSpaceModal && (
        <AssignToOnPremWorkSpaceModal
          setOnPremWorkSpaceModal={setOnPremWorkSpaceModal}
          requestPayload={getAssignToOnPremWorkSpacePayload(selectedRows)}
          onSuccess={() => {
            setSuccessNotification(true)
            setOnPremWorkSpaceModal(false)
            setRefreshDataTable(!refreshDataTable)
          }}
          selectedRows={selectedRows}
          type="service_subscriptions"
        />
      )}
      {manageTagsModal && (
        <ManageSubscriptionTagsModal
          setModalOpen={handleManageModalOpen}
          manageTagsData={manageTagsData}
          setShowErrorNotification={setShowErrorNotification}
          selectedSubscriptionsKey={selectedRows}
          onSuccess={() => {
            // Refresh tags list in filter
            setPullTagsRefresh(!pullTagsRefresh)
            setRefreshDataTable(!refreshDataTable)
          }}
        />
      )}
      {showErrorNotification}
      {successNotification && (
        <OnpremAssignmentSuccessModal
          setSuccessNotification={setSuccessNotification}
        />
      )}
      {showRemoveSubscriptionModal && (
        <RemoveSubscriptionModal
          removeSubscriptions={selectedRows}
          setModalOpen={handleRemoveSubscriptionModalOpen}
          onRemoveSuccess={() => {
            setSubscriptionFeedbackNotification(true)
            setRefreshDataTable(!refreshDataTable)
            setRemoveSubscriptionModal(false)
          }}
          modalRemoveBtn={t('remove_subscriptions_btn')}
          modalHeading={t('remove_subscriptions_modal_heading')}
          modalDescription={t('remove_subscriptions_modal_description')}
        />
      )}
      {showSubscriptionFeedbackNotification && (
        <Notification
          status="normal"
          testId="subscription-removal-notification"
          text={t('subscriptions_removal_notification')}
          onClose={() => {
            setSubscriptionFeedbackNotification(false)
          }}
        />
      )}
    </>
  )
}

ServiceSubscriptionsDatatable.propTypes = {
  serviceSubs: PropTypes.array.isRequired,
  appData: PropTypes.object.isRequired,
  pagination: PropTypes.object.isRequired,
  setRefreshDataTable: PropTypes.func.isRequired,
  refreshDataTable: PropTypes.bool.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  tabName: PropTypes.string.isRequired,
  setShowExportModal: PropTypes.func.isRequired,
  sortProps: PropTypes.object.isRequired,
  setSortProps: PropTypes.func.isRequired,
  setNotification: PropTypes.func.isRequired,
  filterOptions: PropTypes.object.isRequired,
  setFilterOptions: PropTypes.func.isRequired
}

export { ServiceSubscriptionsDatatable }
